import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Factory, Wrench, Package, Cog, Shield, ClipboardCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../utils/mockData';

const Industries = () => {
  const industryIcons = {
    'Manufacturing & Distribution': Factory,
    'Engineering': Cog,
    'Skilled Trades': Wrench,
    'Supply Chain & Logistics': Package,
    'Quality Assurance & Control': ClipboardCheck,
    'Maintenance & Facilities': Shield
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-[#0a1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 break-words px-4">Industries We Serve</h1>
            <p className="text-lg sm:text-xl text-gray-300 px-4">
              Specialized recruiting expertise across key industrial and technical sectors
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                Deep Expertise in Industrial Recruiting
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We understand the unique challenges and requirements of recruiting for industrial, manufacturing, and technical roles.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockData.industries.map((industry, idx) => {
                const IconComponent = industryIcons[industry.name] || Factory;
                
                return (
                  <Card key={industry.name} className="border-2 border-gray-200 hover:border-[#d4a574] transition-all duration-300 hover:shadow-xl">
                    <CardContent className="p-8 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-6">
                        <IconComponent className="text-[#0a1628]" size={32} />
                      </div>
                      <h3 className="text-2xl font-bold text-[#0a1628] mb-3">{industry.name}</h3>
                      <p className="text-gray-600">{industry.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Functional Domains & Key Roles */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">Our Recruiting Scope</h2>
              <p className="text-xl text-gray-600">
                From operators to engineers, we recruit across all levels
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Functional Domains */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-[#0a1628] mb-6 flex items-center">
                  <Factory className="text-[#d4a574] mr-3" size={28} />
                  Functional Domains
                </h3>
                <ul className="space-y-3">
                  {mockData.scope.functionalDomains.map((domain) => (
                    <li key={domain} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#d4a574] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{domain}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Key Roles */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold text-[#0a1628] mb-6 flex items-center">
                  <Wrench className="text-[#d4a574] mr-3" size={28} />
                  Key Roles
                </h3>
                <ul className="space-y-3">
                  {mockData.scope.keyRoles.map((role) => (
                    <li key={role} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#d4a574] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{role}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Coverage */}
      <section className="py-20 bg-[#0a1628] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-8">Nationwide Support with Midwest Roots</h2>
            <p className="text-xl text-gray-300 mb-12">
              Based in Batesville, Indiana, we serve employers throughout the region and across the country
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockData.company.regions.map((region) => (
                <div key={region} className="bg-[#1a2840] p-6 rounded-lg">
                  <p className="text-lg font-semibold text-[#d4a574]">{region}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Veterans Succeed */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
                Why Veterans Excel in These Industries
              </h2>
              <p className="text-xl text-gray-600">
                Military experience translates directly to industrial and technical success
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-4">
                  <Shield className="text-[#0a1628]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-2">Discipline & Reliability</h3>
                <p className="text-gray-600">
                  Veterans bring proven work ethic and commitment to excellence
                </p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-4">
                  <Cog className="text-[#0a1628]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-2">Technical Skills</h3>
                <p className="text-gray-600">
                  Hands-on experience with complex systems and equipment
                </p>
              </div>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#d4a574] rounded-full mb-4">
                  <ClipboardCheck className="text-[#0a1628]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-2">Safety-First Mindset</h3>
                <p className="text-gray-600">
                  Trained to prioritize safety and follow protocols precisely
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-6">
              Find the Right Talent for Your Industry
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Whether you're hiring skilled trades or engineering professionals, we have the expertise and veteran talent pipeline to meet your needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=hire">
                <Button size="lg" className="bg-[#d4a574] text-[#0a1628] hover:bg-[#c49564]">
                  Start Hiring <ArrowRight className="ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-[#0a1628] text-[#0a1628] hover:bg-[#0a1628] hover:text-white">
                  View Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
